import { useCallback, useRef, useState } from "react";
import { Slider } from "@mantine/core";
import { MapLayerPickerPart } from "components/LeafletMap/MapControls/MapLayerPicker";
import { useTranslation } from "react-i18next";
import { useOptions } from "stores/optionsStore/OptionsContext";

type Props = {
  setIgnoreClicks: (value: boolean) => void;
};

export const LabelCountSlider = ({ setIgnoreClicks }: Props) => {
  const {
    updateSaveable,
    state: { mapLabels },
  } = useOptions();
  const [sliderValue, setSliderValue] = useState(mapLabels);
  const { t } = useTranslation();
  const changeEndHandlerClosureBypassFn = useRef<(value: number) => void>();

  // We need to have a bypass function in a ref for the closure that is
  // created in the Slider component's onChangeEnd event handler so the
  // updateSaveable function is always up-to-date
  changeEndHandlerClosureBypassFn.current = (value: number) => {
    updateSaveable({ mapLabels: value });
  };

  const handleChangeEnd = useCallback(
    (value: number) => {
      if (changeEndHandlerClosureBypassFn.current)
        changeEndHandlerClosureBypassFn.current(value);
      setIgnoreClicks(false);
    },
    [setIgnoreClicks]
  );

  const handleChange = (value: number) => {
    setIgnoreClicks(true);
    setSliderValue(value);
  };

  return (
    <MapLayerPickerPart title={t("label_count_slider.title")}>
      <Slider
        mb="lg"
        thumbSize={22}
        onChange={handleChange}
        value={sliderValue}
        onChangeEnd={handleChangeEnd}
        size="sm"
        step={5}
        min={0}
        max={100}
        marks={[
          { value: 0, label: "0%" },
          { value: 25, label: "25%" },
          { value: 50, label: "50%" },
          { value: 75, label: "75%" },
          { value: 100, label: "100%" },
        ]}
        label={(value) => `${value}%`}
        styles={(theme, _, u) => ({
          track: {
            "&:before": {
              [u.dark]: {
                backgroundColor: theme.colors.dark[1],
              },
              [u.light]: {
                backgroundColor: theme.colors.gray[5],
              },
            },
          },
          mark: {
            border: "none",
            width: "12px",
            height: "12px",
            position: "relative",
            left: "-3px",
            top: "-3px",
          },
          markLabel: {
            [u.dark]: {
              color: theme.colors.dark[0],
            },
            [u.light]: {
              color: theme.colors.gray[9],
            },
          },
        })}
      />
    </MapLayerPickerPart>
  );
};
